import { HeaderBackButton, useNavigation } from "expo-router/react-navigation";
import { useRouter } from "expo-router";
import { ChevronLeft, Menu } from "lucide-react-native";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform, useWindowDimensions } from "react-native";

import MainView from "./MainView";
import { DEFAULT_ORGANIZATION_ID } from "../../constants";
import { useAuthOperations } from "../../hooks/useAuthOperations";

import HeaderTitleWithBadge from "@/components/molecules/HeaderTitleWithBadge";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Center } from "@/components/atoms/Center";
import { Heading } from "@/components/atoms/Heading";
import { Icon } from "@/components/atoms/Icon";
import { NativeModal } from "@/components/atoms/NativeModal";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import useAppThemeColor from "@/hooks/useAppThemeColor";

/**
 * Main container component for knowledge graph feature
 * @remarks
 * Uses Apollo reactive variables and hooks for all state management.
 *
 * Rendering strategy: the main chat view is rendered optimistically even while
 * `currentUser` is still loading (undefined). This prevents the chat input from
 * being absent from the DOM during the auth-hydration window, which previously
 * caused E2E tests to time out waiting for the placeholder to become visible.
 * Default-org users are still redirected away via a `useEffect` once their
 * organization ID resolves.
 */
const MainContainer = () => {
  const navigation = useNavigation();
  const router = useRouter();
  const currentUser = useCurrentUser();
  const { headerTintColor } = useAppThemeColor();

  // Use auth operations hook
  const { updateAuthContext } = useAuthOperations();

  const [showClearChatModal, setShowClearChatModal] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  // SE-4010: keeps a resize across the xl breakpoint from re-firing the
  // knowledge_graph_opened event (see effect below).
  const hasLoggedOpenRef = useRef(false);

  const { width } = useWindowDimensions();
  const isDesktop = width >= 1280; // xl breakpoint for iPad compatibility

  // Modal handlers - memoized to prevent unnecessary re-renders
  const handleShowModal = useCallback(() => {
    setShowClearChatModal(true);
  }, []);

  const handleHideModal = useCallback(() => {
    setShowClearChatModal(false);
  }, []);

  // Sidebar handlers - memoized to prevent unnecessary re-renders
  const handleToggleSidebar = useCallback(() => {
    setIsSidebarVisible(prev => !prev);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setIsSidebarVisible(false);
  }, []);

  // Back button icon for iOS/web (extracted for performance)
  const backButtonIcon = useMemo(
    () => (
      <Center className="size-8 p-1">
        <Icon as={ChevronLeft} size="xl" className="size-full" />
      </Center>
    ),
    []
  );

  // Back button press handler — navigates to Home tab since knowledge-graph is now a sibling tab
  const handleBackPress = useCallback(() => {
    router.navigate("/");
  }, [router]);

  // Back image render callback
  const renderBackImage = useCallback(() => backButtonIcon, [backButtonIcon]);

  // Back button for header left (only shown on iOS/web, not Android)
  const isIOSOrWeb = Platform.OS === "ios" || Platform.OS === "web";
  const BackButton = useMemo(
    () =>
      isIOSOrWeb ? (
        <HeaderBackButton
          displayMode="minimal"
          onPress={handleBackPress}
          tintColor="white"
          backImage={renderBackImage}
        />
      ) : null,
    [isIOSOrWeb, handleBackPress, renderBackImage]
  );

  // Header title with experimental badge — matches old Stack.Screen headerTitle config
  const askGeminiHeaderTitle = useCallback(
    () => (
      <HeaderTitleWithBadge
        title="Ask Gemini"
        headerTintColor={headerTintColor}
      />
    ),
    [headerTintColor]
  );

  // Hamburger menu button for header right
  const HamburgerMenuButton = useMemo(
    () => (
      <Box className="pr-2">
        <Button
          variant="outline"
          size="sm"
          onPress={handleToggleSidebar}
          accessibilityLabel="Toggle conversations sidebar"
          icon={Menu}
          iconSize="sm"
          iconTone="secondary"
          className="size-8 p-0"
        />
      </Box>
    ),
    [handleToggleSidebar]
  );

  // Clear Chat button component - memoized to prevent recreation
  const _ClearChatButton = useMemo(
    () => (
      <Box className="pr-4">
        <Pressable
          onPress={handleShowModal}
          className="rounded-none bg-surface-muted px-3 py-1"
        >
          <Text variant="body-strong" className="text-content-secondary">
            Clear Chat
          </Text>
        </Pressable>
      </Box>
    ),
    [handleShowModal]
  );

  // Clear Chat Modal component - memoized to prevent recreation
  const ClearChatModal = useMemo(
    () => (
      <NativeModal
        visible={showClearChatModal}
        transparent
        animationType="fade"
        onRequestClose={handleHideModal}
      >
        <Box className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 p-5">
          <Box className="min-w-72 gap-4 rounded-sm bg-surface-base p-6 shadow-hard-2">
            <Heading
              variant="title-lg"
              className="text-center text-content-primary"
            >
              Clear Chat
            </Heading>

            <Text
              variant="title-sm"
              className="text-center leading-6 text-content-secondary"
            >
              Are you sure you want to start a new chat session? This will clear
              your current conversation.
            </Text>

            <Box className="flex flex-row justify-center gap-3 pt-2">
              <Button
                variant="outline"
                onPress={handleHideModal}
                label="Cancel"
                className="min-w-20"
              />
            </Box>
          </Box>
        </Box>
      </NativeModal>
    ),
    [showClearChatModal, handleHideModal]
  );

  // Update chat store with current user context whenever auth state changes
  useEffect(() => {
    updateAuthContext(currentUser).catch(() => {
      // Ignore storage errors - auth context will use in-memory fallback
    });
  }, [currentUser, updateAuthContext]);

  // SE-4010: feature-scoped analytics — once per mount (ref-guarded).
  // Gated on a resolved, non-Default org so the redirect effect below never
  // records a view for users who cannot access the feature.
  useEffect(() => {
    if (
      !currentUser ||
      currentUser.organizationId === DEFAULT_ORGANIZATION_ID
    ) {
      return;
    }
    if (hasLoggedOpenRef.current) return;
    hasLoggedOpenRef.current = true;
    void AnalyticsTracker.scouting.logKnowledgeGraphOpened({
      layout: isDesktop ? "desktop" : "mobile",
    });
  }, [currentUser, isDesktop]);

  // Redirect Default organization users away from this feature
  useEffect(() => {
    if (
      currentUser?.organizationId &&
      currentUser?.organizationId === DEFAULT_ORGANIZATION_ID
    ) {
      router.replace("/");
    }
  }, [currentUser?.organizationId, router]);

  // Set header options — title, back button, and hamburger menu
  useEffect(() => {
    navigation.setOptions({
      headerTitle: askGeminiHeaderTitle,
      headerTitleAlign: "center",
      headerLeft: () => BackButton,
      headerRight: () => HamburgerMenuButton,
    });
  }, [navigation, askGeminiHeaderTitle, BackButton, HamburgerMenuButton]);

  // Don't render main view if user is confirmed to be in the Default org.
  // When currentUser is undefined (still loading), we render the main view
  // optimistically so the chat UI is visible immediately after navigation.
  // The redirect effect above fires once the user context resolves,
  // navigating default-org users away before they can interact.
  if (currentUser?.organizationId === DEFAULT_ORGANIZATION_ID) {
    return null;
  }

  return (
    <>
      <MainView
        isSidebarVisible={isSidebarVisible}
        isDesktop={isDesktop}
        onCloseSidebar={handleCloseSidebar}
      />
      {ClearChatModal}
    </>
  );
};

export default MainContainer;
